'use client';

import { useEffect, useCallback, useMemo, useRef, useState, createContext, useContext, type ReactNode } from 'react';
import { useConfigValue } from '@miadvg/config/hooks';
import {
  COBROWSE_SESSION_ID_RESOLVE_MS,
  resolveCobrowseSessionIdAfterCode,
} from '@/lib/cobrowse/resolve-session-id-after-code';
import { captureClientPosthogEvent } from '@/lib/utils/capture-client-posthog';

interface CobrowseSession {
  code: () => string;
  id: () => string;
  end: () => void;
  isActive: () => boolean;
  activate: () => Promise<void>;
}

interface CobrowseSDK {
  api: string;
  license: string;
  customData: Record<string, string>;
  confirmSession: (() => Promise<boolean>) | null;
  showSessionControls: (() => void) | null;
  hideSessionControls: (() => void) | null;
  start: () => Promise<void>;
  stop: () => void;
  createSessionCode: () => Promise<string>;
  currentSession: CobrowseSession | null;
  on: (event: string, callback: (session: CobrowseSession) => void) => void;
  off: (event: string, callback: (session: CobrowseSession) => void) => void;
  client: () => Promise<CobrowseSDK>;
}

declare global {
  interface Window {
    CobrowseIO?: CobrowseSDK;
  }
}

interface CobrowseContextType {
  isReady: boolean;
  isActive: boolean;
  sessionCode: string | null;
  createCode: () => Promise<{ code: string; sessionId: string } | null>;
  endSession: () => void;
  setAutoAccept: (enabled: boolean) => void;
  error: string | null;
}

const CobrowseContext = createContext<CobrowseContextType>({
  isReady: false,
  isActive: false,
  sessionCode: null,
  createCode: async () => null,
  endSession: () => {},
  setAutoAccept: () => {},
  error: null,
});

export function useCobrowse() {
  return useContext(CobrowseContext);
}

interface CobrowseProviderProps {
  children: ReactNode;
  opportunityId?: string;
  onSessionStart?: (code: string) => void;
  onSessionEnd?: () => void;
  enabled?: boolean;
}

export function CobrowseProvider({
  children,
  opportunityId,
  onSessionStart,
  onSessionEnd,
  enabled = true,
}: CobrowseProviderProps) {
  const [isReady, setIsReady] = useState(false);
  const [isActive, setIsActive] = useState(false);
  const [sessionCode, setSessionCode] = useState<string | null>(null);
  const [, setAutoAccept] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const startPromiseRef = useRef<Promise<void> | null>(null);

  const cobrowseLicense = useConfigValue('NEXT_PUBLIC_COBROWSE_LICENSE');

  const customData = useMemo(() => ({
    user_id: opportunityId || '',
    user_name: opportunityId ? `Client-${opportunityId.slice(-6)}` : 'Client',
    device_name: 'Web Browser',
  }), [opportunityId]);

  const configureSdk = useCallback(
    (cobrowse: CobrowseSDK, licenseKey: string) => {
      cobrowse.license = licenseKey;
      cobrowse.api = 'https://cobrowse.io';
      cobrowse.customData = customData;
      cobrowse.confirmSession = () => Promise.resolve(true);
      cobrowse.showSessionControls = function () {};
      cobrowse.hideSessionControls = function () {};
    },
    [customData],
  );

  const ensureStarted = useCallback(
    async (reason: 'auto' | 'createCode') => {
      const licenseKey = cobrowseLicense;
      if (!licenseKey) {
        setError('Cobrowse not configured');
        throw new Error('Cobrowse not configured');
      }

      if (startPromiseRef.current) {
        await startPromiseRef.current;
        return;
      }

      startPromiseRef.current = (async () => {
        const alreadyLoaded = window.CobrowseIO;
        if (alreadyLoaded) {
          configureSdk(alreadyLoaded, licenseKey);
          await alreadyLoaded.start();
          setIsReady(true);
          return;
        }

        await new Promise<void>((resolve, reject) => {
          const existing = document.querySelector<HTMLScriptElement>(
            'script[src="https://js.cobrowse.io/CobrowseIO.js"]',
          );
          if (existing) {
            existing.addEventListener('load', () => resolve(), { once: true });
            existing.addEventListener('error', () => reject(new Error('Failed to load Cobrowse SDK')), { once: true });
            return;
          }

          const script = document.createElement('script');
          script.src = 'https://js.cobrowse.io/CobrowseIO.js';
          script.crossOrigin = 'anonymous';
          script.async = true;
          script.onload = () => resolve();
          script.onerror = () => reject(new Error('Failed to load Cobrowse SDK'));
          document.head.appendChild(script);
        });

        const cobrowse = window.CobrowseIO;
        if (!cobrowse) throw new Error('Cobrowse SDK missing after load');

        configureSdk(cobrowse, licenseKey);
        await cobrowse.start();
        setIsReady(true);
      })().catch((err) => {
        console.error(`[Cobrowse] Failed to start (${reason}):`, err);
        setError('Failed to start Cobrowse');
        startPromiseRef.current = null;
        throw err;
      });

      await startPromiseRef.current;
    },
    [cobrowseLicense, configureSdk],
  );

  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;
    const start = () => {
      if (!cancelled) void ensureStarted('auto').catch(() => {});
    };
    const handle = typeof requestIdleCallback === 'function'
      ? requestIdleCallback(start)
      : setTimeout(start, 2000);
    return () => {
      cancelled = true;
      if (typeof cancelIdleCallback === 'function' && typeof handle === 'number') {
        cancelIdleCallback(handle);
      } else {
        clearTimeout(handle as ReturnType<typeof setTimeout>);
      }
    };
  }, [enabled, ensureStarted]);

  useEffect(() => {
    if (!isReady || !window.CobrowseIO) return;

    const handleSessionStart = (session: CobrowseSession) => {
      const code = session.code();
      setSessionCode(code);
      setIsActive(true);
      onSessionStart?.(code);
    };

    const handleSessionEnd = () => {
      setSessionCode(null);
      setIsActive(false);
      onSessionEnd?.();
    };

    window.CobrowseIO.on('session.started', handleSessionStart);
    window.CobrowseIO.on('session.ended', handleSessionEnd);

    return () => {
      window.CobrowseIO?.off('session.started', handleSessionStart);
      window.CobrowseIO?.off('session.ended', handleSessionEnd);
    };
  }, [isReady, onSessionStart, onSessionEnd]);

  const createCode = useCallback(async (): Promise<{ code: string; sessionId: string } | null> => {
    try {
      captureClientPosthogEvent('cobrowse_create_code_started', { opportunityId, isReady });
      if (!isReady || !window.CobrowseIO) {
        await ensureStarted('createCode');
      }
      if (!window.CobrowseIO) {
        setError('Cobrowse not ready');
        return null;
      }

      if (window.CobrowseIO.currentSession?.isActive()) {
        window.CobrowseIO.currentSession.end();
        await new Promise(resolve => setTimeout(resolve, 500));
      }

      const code = await window.CobrowseIO.createSessionCode();
      const sessionId =
        (await resolveCobrowseSessionIdAfterCode(window.CobrowseIO, COBROWSE_SESSION_ID_RESOLVE_MS)) || '';

      captureClientPosthogEvent('cobrowse_create_code_succeeded', {
        opportunityId,
        hasCode: Boolean(code),
        hasSessionId: Boolean(sessionId),
      });

      setSessionCode(code);
      setIsActive(true);
      onSessionStart?.(code);

      return { code, sessionId };
    } catch (err) {
      captureClientPosthogEvent('cobrowse_create_code_failed', {
        opportunityId,
        error: err instanceof Error ? err.message : 'Unknown error',
      });
      console.error('[Cobrowse] Failed to create session code:', err);
      setError('Failed to create session code');
      return null;
    }
  }, [ensureStarted, isReady, onSessionStart, opportunityId]);

  const endSession = useCallback(() => {
    if (window.CobrowseIO?.currentSession?.isActive()) {
      window.CobrowseIO.currentSession.end();
    }
    setSessionCode(null);
    setIsActive(false);
  }, []);

  return (
    <CobrowseContext.Provider
      value={{ isReady, isActive, sessionCode, createCode, endSession, setAutoAccept, error }}
    >
      {children}
    </CobrowseContext.Provider>
  );
}
